import React, { useMemo, useState } from 'react';

import { Alert, Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import { useGetPlaces } from '~/hooks/apis/search/useGetPlaces';
import { useGetDestinationContentTagLocationIds } from '~/hooks/apis/tags/useDestinationContentTagOrder';

import DestinationContentTagOrderEditor from './DestinationContentTagOrderEditor';

type LocationOption = { id: string; label: string };

export default function ContentTagRankingTab() {
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const { data: locationIds = [], error, isLoading } = useGetDestinationContentTagLocationIds();
  const { isError: isLoadingLocationsError, isFetching: isLoadingLocations, places } = useGetPlaces(locationIds);
  const locations = useMemo(
    () =>
      places
        .map((place) => ({ id: place.placeId, label: place.name }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [places],
  );

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Select a location to manage the customer-facing order of its Content Tags.
      </Typography>
      {isLoading || isLoadingLocations ? (
        <CircularProgress size={20} />
      ) : error || isLoadingLocationsError ? (
        <Alert severity="error">Unable to load locations with Content Tags.</Alert>
      ) : (
        <Autocomplete
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => setSelectedLocation(value)}
          options={locations}
          renderInput={(params) => (
            <TextField {...params} label="Location" placeholder="Select a location..." size="small" />
          )}
          sx={{ maxWidth: 480 }}
          value={selectedLocation}
        />
      )}
      {selectedLocation && (
        <DestinationContentTagOrderEditor
          destinationId={selectedLocation.id}
          destinationName={selectedLocation.label}
        />
      )}
    </Stack>
  );
}
